import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
	Dialog,
	DialogContent,
	IconButton,
	Box,
	CircularProgress,
	FormControlLabel,
	Switch,
	Pagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { FaChartBar, FaFutbol, FaExchangeAlt, FaRegClock, FaUsers, FaSearch, FaTimes } from 'react-icons/fa';
import matchService from '../services/matchService';
import predictionService from '../services/predictionService';
import { AuthContext } from './AuthContext';
import { POINT_STYLES } from '../utils/scoringCalculator';
import { translateTeamName } from '../utils/roundTranslator';
import { getDominantColor, deconflictColors, rgbToCss, readableOnLight } from '../utils/badgeColor';
import '../styles/MatchStatisticsModal.css';

const DEFAULT_HOME = '#2563eb';
const DEFAULT_AWAY = '#dc2626';

const BACKEND_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

// Počet tipů ostatních hráčů na jednu stránku v záložce Tipy.
const TIPS_PAGE_SIZE = 10;
// Prodleva hledání podle jména, ať se backend nevolá při každém úhozu.
const TIPS_SEARCH_DEBOUNCE_MS = 300;

const STAT_TYPE_LABELS = {
	'Shots on Goal': 'Střely na bránu',
	'Shots off Goal': 'Střely mimo bránu',
	'Total Shots': 'Celkem střel',
	'Blocked Shots': 'Zblokované střely',
	'Shots insidebox': 'Střely z vápna',
	'Shots outsidebox': 'Střely mimo vápno',
	'Fouls': 'Fauly',
	'Corner Kicks': 'Rohové kopy',
	'Offsides': 'Ofsajdy',
	'Ball Possession': 'Držení míče',
	'Yellow Cards': 'Žluté karty',
	'Red Cards': 'Červené karty',
	'Goalkeeper Saves': 'Zákroky brankáře',
	'Total passes': 'Celkem přihrávek',
	'Passes accurate': 'Přesné přihrávky',
	'Passes %': 'Přesnost přihrávek',
	'expected_goals': 'Očekávané góly (xG)',
	'goals_prevented': 'Zabráněné góly'
};

const formatValue = (v) => (v === null || v === undefined ? '-' : v);

const toNumber = (v) => {
	if (v === null || v === undefined) return 0;
	const n = parseFloat(String(v).replace('%', ''));
	return isNaN(n) ? 0 : n;
};

// Porovnání názvů týmů (události z API mají jiné id týmu než interní Scoreo
// model, takže párujeme podle jména – stejně jako extractRedCards v MatchCard).
const normName = (s) => (s || '').trim().toLowerCase();

// Minuta události vč. nastaveného času: { elapsed: 90, extra: 2 } → "90+2'".
const formatMinute = (time) => {
	if (!time || time.elapsed === null || time.elapsed === undefined) return '';
	return time.extra ? `${time.elapsed}+${time.extra}'` : `${time.elapsed}'`;
};

const MatchStatisticsModal = ({
	open,
	matchId,
	homeTeam,
	awayTeam,
	scoreHome,
	scoreAway,
	onClose,
	hasStatistics = true,
	leagueId = null,
	showTips = false,
	scoringType,
	multiplier = 1,
	status = null
}) => {
	const { t } = useTranslation();
	const { userName: currentUserName } = useContext(AuthContext);

	// Záložky Statistiky/Průběh jen když pro zápas existují statistiky; Tipy jen
	// v kontextu ligy (tipy ostatních jsou vázané na ligu).
	const tabs = useMemo(() => {
		const list = [];
		if (hasStatistics) list.push('stats', 'timeline');
		if (showTips && leagueId) list.push('tips');
		return list;
	}, [hasStatistics, showTips, leagueId]);
	const defaultTab = tabs[0] || 'stats';
	const [activeTab, setActiveTab] = useState(defaultTab);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);
	const [teamColors, setTeamColors] = useState(null);

	// Události se stahují líně – až když uživatel poprvé přepne na záložku Průběh.
	const [eventsLoading, setEventsLoading] = useState(false);
	const [eventsError, setEventsError] = useState(null);
	const [eventsData, setEventsData] = useState(null);
	const [eventsFetched, setEventsFetched] = useState(false);
	const [showSubstitutions, setShowSubstitutions] = useState(false);

	// Tipy ostatních hráčů ligy – stránkovaně ze serveru, načítají se až na záložce Tipy.
	const [tipsPage, setTipsPage] = useState(0);
	// Hledání podle jména: tipsSearch = text v poli, tipsQuery = odeslaný (po debounce).
	const [tipsSearch, setTipsSearch] = useState('');
	const [tipsQuery, setTipsQuery] = useState('');
	const [tipsRows, setTipsRows] = useState(null);
	const [tipsTotal, setTipsTotal] = useState(0);
	const [tipsLoading, setTipsLoading] = useState(false);
	const [tipsError, setTipsError] = useState(null);

	// Reset stavu až po doběhnutí animace zavření (onExited) – jinak by obsah
	// během zavírací animace problikl na první záložku.
	const resetState = () => {
		setActiveTab(defaultTab);
		setEventsFetched(false);
		setEventsData(null);
		setEventsError(null);
		setShowSubstitutions(false);
		setTipsPage(0);
		setTipsSearch('');
		setTipsQuery('');
		setTipsRows(null);
		setTipsTotal(0);
		setTipsError(null);
	};

	// Sada záložek se může změnit, i když je modal zavřený (např. dorazí souhrn
	// statistik) – aktivní záložka musí vždy existovat.
	useEffect(() => {
		if (!tabs.includes(activeTab)) setActiveTab(defaultTab);
	}, [tabs, activeTab, defaultTab]);

	// Týmové barvy (po deconfliktu, stejné jako na MatchCard) pro obarvení modálu.
	useEffect(() => {
		if (!open || !homeTeam?.id || !awayTeam?.id) return undefined;
		let cancelled = false;
		Promise.all([
			getDominantColor(homeTeam.id, homeTeam.name),
			getDominantColor(awayTeam.id, awayTeam.name),
		]).then(([h, a]) => {
			if (cancelled) return;
			setTeamColors(deconflictColors(h.palette, a.palette));
		});
		return () => { cancelled = true; };
	}, [open, homeTeam?.id, homeTeam?.name, awayTeam?.id, awayTeam?.name]);

	const homeColor = teamColors ? rgbToCss(readableOnLight(teamColors.home)) : DEFAULT_HOME;
	const awayColor = teamColors ? rgbToCss(readableOnLight(teamColors.away)) : DEFAULT_AWAY;

	useEffect(() => {
		if (!open || !matchId || !hasStatistics) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		setData(null);

		matchService.getMatchStatistics(matchId)
			.then((res) => {
				if (cancelled) return;
				if (!res || !res.teams || res.teams.length < 2) {
					setError(t('statisticsModal.notAvailable', { defaultValue: 'Statistiky pro tento zápas zatím nejsou k dispozici.' }));
					return;
				}
				setData(res);
			})
			.catch(() => {
				if (cancelled) return;
				setError(t('statisticsModal.loadFailed', { defaultValue: 'Nepodařilo se načíst statistiky.' }));
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, matchId, hasStatistics, t]);

	// Líné načtení událostí při prvním přepnutí na záložku Průběh.
	useEffect(() => {
		if (!open || !matchId || activeTab !== 'timeline' || eventsFetched) return;
		let cancelled = false;
		setEventsLoading(true);
		setEventsError(null);
		setEventsFetched(true);

		matchService.getMatchEvents(matchId)
			.then((res) => {
				if (cancelled) return;
				const list = res?.response;
				if (!Array.isArray(list) || list.length === 0) {
					setEventsError(t('eventsModal.notAvailable', { defaultValue: 'Průběh zápasu zatím není k dispozici.' }));
					return;
				}
				setEventsData(list);
			})
			.catch(() => {
				if (cancelled) return;
				setEventsError(t('eventsModal.loadFailed', { defaultValue: 'Nepodařilo se načíst průběh zápasu.' }));
			})
			.finally(() => {
				if (!cancelled) setEventsLoading(false);
			});

		return () => { cancelled = true; };
		// `eventsFetched` záměrně NENÍ v deps: nastavujeme ho uvnitř efektu, a kdyby
		// byl v deps, efekt by se hned spustil podruhé, první (zrušený) běh by kvůli
		// `cancelled` nikdy nevypnul loading a spinner by se točil donekonečna.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, matchId, activeTab, t]);

	// Tipy ostatních – načtení aktuální stránky při otevření záložky nebo změně stránky.
	useEffect(() => {
		if (!open || !matchId || !leagueId || activeTab !== 'tips') return undefined;
		let cancelled = false;
		setTipsLoading(true);
		setTipsError(null);

		predictionService.getPredictionsByLeagueAndMatch(leagueId, matchId, tipsPage, TIPS_PAGE_SIZE, tipsQuery)
			.then(({ items, total }) => {
				if (cancelled) return;
				setTipsRows(items);
				setTipsTotal(total);
			})
			.catch(() => {
				if (cancelled) return;
				setTipsError(t('tipsModal.loadFailed', { defaultValue: 'Nepodařilo se načíst tipy hráčů.' }));
			})
			.finally(() => {
				if (!cancelled) setTipsLoading(false);
			});

		return () => { cancelled = true; };
	}, [open, matchId, leagueId, activeTab, tipsPage, tipsQuery, t]);

	// Debounce hledání – po změně dotazu začínáme zase od první stránky.
	useEffect(() => {
		const query = tipsSearch.trim();
		if (query === tipsQuery) return undefined;
		const id = setTimeout(() => {
			setTipsQuery(query);
			setTipsPage(0);
		}, TIPS_SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(id);
	}, [tipsSearch, tipsQuery]);

	const [home, away] = data?.teams || [];

	const types = useMemo(() => (home ? home.statistics.map((s) => s.type) : []), [home]);

	const lookup = (team, type) => {
		const found = team?.statistics?.find((s) => s.type === type);
		return found ? found.value : null;
	};

	// Domácí tým v událostech – id z API se neshoduje s interním, proto ho
	// odvodíme z první události, jejíž název týmu odpovídá domácímu týmu zápasu.
	// Z toho pak párujeme i ostatní události (i kdyby se název mírně lišil).
	const eventsHomeId = useMemo(() => {
		if (!eventsData) return null;
		const key = normName(homeTeam?.name);
		const match = eventsData.find((e) => normName(e.team?.name) === key);
		return match?.team?.id ?? null;
	}, [eventsData, homeTeam?.name]);

	const isHomeEvent = (ev) => {
		if (eventsHomeId != null) return ev.team?.id === eventsHomeId;
		// fallback na porovnání podle jména, kdyby se home id nepodařilo odvodit
		return normName(ev.team?.name) === normName(homeTeam?.name);
	};

	// Průběžné skóre v okamžiku každého gólu (index události → "h:a"). Vlastní góly
	// se počítají soupeři; nezapočítáváme neproměněné penalty ani penaltový rozstřel.
	const goalRunningScores = useMemo(() => {
		const map = {};
		if (!eventsData) return map;
		let h = 0;
		let a = 0;
		eventsData.forEach((ev, idx) => {
			if (ev.type !== 'Goal') return;
			const detail = (ev.detail || '').toLowerCase();
			const comments = (ev.comments || '').toLowerCase();
			if (detail.includes('missed') || comments.includes('penalty shootout')) return;
			const home = eventsHomeId != null
				? ev.team?.id === eventsHomeId
				: normName(ev.team?.name) === normName(homeTeam?.name);
			const creditHome = detail.includes('own') ? !home : home;
			if (creditHome) h += 1; else a += 1;
			map[idx] = `${h}:${a}`;
		});
		return map;
	}, [eventsData, eventsHomeId, homeTeam?.name]);

	// Logo týmu – primárně stejný badge ze Scoreo backendu jako na MatchCard (klubový
	// znak / vlajka, funguje i pro hokej bez statistik). Když se nenačte, zkusíme logo
	// ze statistik nebo z událostí (API-Football) podle názvu.
	const eventLogo = (teamName) => {
		const key = normName(teamName);
		return eventsData?.find((e) => normName(e.team?.name) === key)?.team?.logo;
	};
	const [failedBadges, setFailedBadges] = useState(() => new Set());
	const markBadgeFailed = (url) => setFailedBadges((prev) => new Set(prev).add(url));
	const pickBadge = (team, statsTeam) => [
		team?.id != null ? `${BACKEND_URL}/badges/${team.id}.png` : null,
		statsTeam?.teamBadge,
		eventLogo(team?.name),
		team?.logo,
	].find((url) => url && !failedBadges.has(url));
	const homeName = translateTeamName(home?.teamName || homeTeam?.name || '', t);
	const awayName = translateTeamName(away?.teamName || awayTeam?.name || '', t);
	const homeBadge = pickBadge(homeTeam, home);
	const awayBadge = pickBadge(awayTeam, away);

	const renderCompareBar = (type) => {
		const hv = toNumber(lookup(home, type));
		const av = toNumber(lookup(away, type));
		const total = hv + av;
		const hp = total > 0 ? (hv / total) * 100 : 50;
		const ap = total > 0 ? (av / total) * 100 : 50;
		return (
			<div className="msm-compare-bar">
				<div className="msm-compare-seg msm-compare-home" style={{ width: `${hp}%` }} />
				<div className="msm-compare-seg msm-compare-away" style={{ width: `${ap}%` }} />
			</div>
		);
	};

	// --- Průběh (timeline) ---

	// Ikonka + popisek typu události. Góly se barví dominantní barvou týmu.
	const renderEventIcon = (ev, teamColor) => {
		const detail = (ev.detail || '').toLowerCase();
		if (ev.type === 'Goal') {
			// Neproměněná penalta není gól – šedý přeškrtnutý míč, ne v barvě týmu.
			if (detail.includes('missed')) {
				return <span className="msm-ev-icon msm-ev-miss"><FaFutbol /></span>;
			}
			return <span className="msm-ev-icon msm-ev-goal" style={{ color: teamColor }}><FaFutbol /></span>;
		}
		if (ev.type === 'Card') {
			const isRed = detail.includes('red');
			return <span className={`msm-ev-icon msm-ev-card ${isRed ? 'msm-card-red' : 'msm-card-yellow'}`} />;
		}
		if (ev.type === 'subst') {
			return <span className="msm-ev-icon msm-ev-subst"><FaExchangeAlt /></span>;
		}
		// Var a ostatní
		return <span className="msm-ev-icon msm-ev-var">VAR</span>;
	};

	const renderEventBody = (ev, teamColor, runningScore) => {
		if (ev.type === 'subst') {
			// API-Football: player = hráč přicházející na hřiště, assist = střídaný hráč.
			// Střídání záměrně bez barev – jen černý text.
			return (
				<>
					<div className="msm-ev-main msm-sub-in">{ev.player?.name || '?'}</div>
					{ev.assist?.name && <div className="msm-ev-sub msm-sub-out">{ev.assist.name}</div>}
				</>
			);
		}
		const detailLabel = t(`eventDetail.${ev.detail}`, { defaultValue: ev.detail || '' });
		const isGoal = ev.type === 'Goal';
		// Neproměněná penalta: nepočítá se do skóre ani se nebarví jako gól.
		const isMissedPenalty = isGoal && (ev.detail || '').toLowerCase().includes('missed');
		const isScoredGoal = isGoal && !isMissedPenalty;
		return (
			<>
				<div className="msm-ev-main" style={isScoredGoal ? { color: teamColor } : undefined}>
					<span>{ev.player?.name || detailLabel}</span>
					{isScoredGoal && runningScore && (
						<span className="msm-ev-score">{runningScore}</span>
					)}
				</div>
				{isScoredGoal && ev.assist?.name && (
					<div className="msm-ev-sub">
						{t('eventsModal.assist', { defaultValue: 'asistence' })}: {ev.assist.name}
					</div>
				)}
				{(isMissedPenalty || ev.type === 'Card' || ev.type === 'Var') && (
					<div className="msm-ev-sub">{detailLabel}</div>
				)}
			</>
		);
	};

	const renderTimelineRow = (ev, idx) => {
		const isHome = isHomeEvent(ev);
		const teamColor = isHome ? homeColor : awayColor;
		const runningScore = goalRunningScores[idx];
		return (
			<div className={`msm-ev-row ${isHome ? 'msm-ev-home' : 'msm-ev-away'}`} key={`${ev.time?.elapsed}-${idx}`}>
				<div className="msm-ev-side msm-ev-side-left">
					{isHome && renderEventBody(ev, teamColor, runningScore)}
				</div>
				<div className="msm-ev-center">
					{renderEventIcon(ev, teamColor)}
					<span className="msm-ev-minute">{formatMinute(ev.time)}</span>
				</div>
				<div className="msm-ev-side msm-ev-side-right">
					{!isHome && renderEventBody(ev, teamColor, runningScore)}
				</div>
			</div>
		);
	};

	// Stav zápasu pod skóre – stejně jako na MatchCard: „Konec", u penalt jejich
	// skóre, u prodloužení skóre po základní době, u živého zápasu minuta.
	const renderStatus = () => {
		if (!status?.matchStatus) return null;
		const {
			matchStatus, penaltyScoreHome, penaltyScoreAway,
			regularTimeScoreHome, regularTimeScoreAway, progress, inProgress
		} = status;
		return (
			<div className="msm-status">
				<span>{t(`matchStatus.${matchStatus}`, { defaultValue: matchStatus })}</span>
				{matchStatus === 'PEN' && penaltyScoreHome != null && penaltyScoreAway != null && (
					<span> ({penaltyScoreHome}:{penaltyScoreAway})</span>
				)}
				{(matchStatus === 'AET' || matchStatus === 'AOT') &&
					regularTimeScoreHome != null && regularTimeScoreAway != null && (
						<span> ({regularTimeScoreHome}:{regularTimeScoreAway})</span>
					)}
				{inProgress && progress > 0 && (
					<span className="msm-status-live"> ({progress}<span className="blinking-minute">′</span>)</span>
				)}
			</div>
		);
	};

	const renderTeamsHeader = (titleIcon, titleText) => (
		<div className="msm-header">
			<div className="msm-header-title">
				{titleIcon}
				<span>{titleText}</span>
			</div>
			<div className="msm-teams">
				<div className="msm-team-block">
					{homeBadge && <img src={homeBadge} alt={homeName} className="msm-team-logo" onError={() => markBadgeFailed(homeBadge)} />}
					<div className="msm-team-name" style={{ color: homeColor }}>{homeName}</div>
				</div>
				<div className="msm-score-block">
					{(scoreHome !== null && scoreHome !== undefined && scoreAway !== null && scoreAway !== undefined)
						? <div className="msm-score">{scoreHome} : {scoreAway}</div>
						: <div className="msm-vs">VS</div>}
					{renderStatus()}
				</div>
				<div className="msm-team-block">
					{awayBadge && <img src={awayBadge} alt={awayName} className="msm-team-logo" onError={() => markBadgeFailed(awayBadge)} />}
					<div className="msm-team-name" style={{ color: awayColor }}>{awayName}</div>
				</div>
			</div>
		</div>
	);

	const renderStatsTab = () => {
		if (loading) {
			return (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
					<CircularProgress />
				</Box>
			);
		}
		if (error) return <div className="msm-error">{String(error)}</div>;
		if (!data) return null;
		return (
			<>
				{renderTeamsHeader(<FaChartBar />, t('statisticsModal.title', { defaultValue: 'Statistiky zápasu' }))}
				<div className="msm-table">
					{types.map((type) => (
						<div className="msm-row" key={type}>
							<div className="msm-value msm-value-home">{formatValue(lookup(home, type))}</div>
							<div className="msm-label">
								{t(`stats.${type}`, { defaultValue: STAT_TYPE_LABELS[type] || type })}
							</div>
							<div className="msm-value msm-value-away">{formatValue(lookup(away, type))}</div>
							{renderCompareBar(type)}
						</div>
					))}
				</div>
			</>
		);
	};

	// --- Tipy ostatních hráčů ---

	const isCasual = (scoringType || '').toUpperCase() === 'CASUAL';
	const pointsMultiplier = Number(multiplier) > 1 ? Number(multiplier) : 1;

	// Barva bodů podle základního stupně (body jsou už vynásobené násobičem kola),
	// stejně jako barvení karty v MatchCard. Mimo CASUAL ligy neutrální.
	const pointsColor = (points) => {
		if (!isCasual || typeof points !== 'number') return undefined;
		return POINT_STYLES[points / pointsMultiplier]?.color;
	};

	const renderTipScore = (row) => {
		// Tipnutá remíza s postupujícím → „X" u strany, která podle tipu postupuje.
		const showAdvancing = row.drawInRegularTime === true && row.advancingTeamId != null;
		const homeAdvancing = showAdvancing && row.advancingTeamId === homeTeam?.id;
		const awayAdvancing = showAdvancing && row.advancingTeamId === awayTeam?.id;
		return (
			<>
				{row.predictedHomeScore}
				{homeAdvancing && <sup className="msm-tip-adv">X</sup>}
				{' : '}
				{row.predictedAwayScore}
				{awayAdvancing && <sup className="msm-tip-adv">X</sup>}
			</>
		);
	};

	const renderTipsTab = () => {
		const pageCount = Math.ceil(tipsTotal / TIPS_PAGE_SIZE);
		let body;
		if (tipsLoading && !tipsRows) {
			body = (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
					<CircularProgress />
				</Box>
			);
		} else if (tipsError) {
			body = <div className="msm-error">{String(tipsError)}</div>;
		} else if (!tipsRows || tipsRows.length === 0) {
			body = (
				<div className="msm-error">
					{tipsQuery
						? t('tipsModal.noMatch', { defaultValue: 'Žádný hráč neodpovídá hledání.' })
						: t('tipsModal.empty', { defaultValue: 'Na tento zápas zatím nikdo netipoval.' })}
				</div>
			);
		} else {
			body = (
				<div className={`msm-tips ${tipsLoading ? 'is-loading' : ''}`}>
					<div className="msm-tip-row msm-tip-head">
						<span>{t('tipsModal.player', { defaultValue: 'Hráč' })}</span>
						<span className="msm-tip-score">{t('tipsModal.tip', { defaultValue: 'Tip' })}</span>
						<span className="msm-tip-points">{t('tipsModal.points', { defaultValue: 'Body' })}</span>
					</div>
					{tipsRows.map((row) => {
						const isMe = currentUserName && row.username === currentUserName;
						return (
							<div className={`msm-tip-row ${isMe ? 'is-me' : ''}`} key={row.userId}>
								<span className="msm-tip-user" title={row.username}>
									{row.username}
									{isMe && <span className="msm-tip-me">{t('tipsModal.you', { defaultValue: '(ty)' })}</span>}
								</span>
								<span className="msm-tip-score">{renderTipScore(row)}</span>
								<span className="msm-tip-points" style={{ color: pointsColor(row.points) }}>
									{row.points ?? '–'}
								</span>
							</div>
						);
					})}
				</div>
			);
		}

		return (
			<>
				{renderTeamsHeader(<FaUsers />, t('tipsModal.title', { defaultValue: 'Tipy hráčů' }))}
				<div className="msm-tip-search">
					<FaSearch className="msm-tip-search-icon" aria-hidden="true" />
					<input
						type="search"
						value={tipsSearch}
						onChange={(e) => setTipsSearch(e.target.value)}
						placeholder={t('tipsModal.searchPlaceholder', { defaultValue: 'Hledat hráče…' })}
						aria-label={t('tipsModal.searchPlaceholder', { defaultValue: 'Hledat hráče…' })}
						maxLength={50}
					/>
					{tipsSearch && (
						<button
							type="button"
							className="msm-tip-search-clear"
							onClick={() => setTipsSearch('')}
							aria-label={t('tipsModal.clearSearch', { defaultValue: 'Smazat hledání' })}
						>
							<FaTimes />
						</button>
					)}
				</div>
				{body}
				{pageCount > 1 && (
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
						<Pagination
							count={pageCount}
							page={tipsPage + 1}
							onChange={(_, p) => setTipsPage(p - 1)}
							size="small"
							siblingCount={0}
							disabled={tipsLoading}
						/>
					</Box>
				)}
			</>
		);
	};

	const renderTimelineTab = () => {
		if (eventsLoading) {
			return (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
					<CircularProgress />
				</Box>
			);
		}
		if (eventsError) return <div className="msm-error">{String(eventsError)}</div>;
		if (!eventsData) return null;
		return (
			<>
				{renderTeamsHeader(<FaRegClock />, t('eventsModal.title', { defaultValue: 'Průběh zápasu' }))}
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
					<FormControlLabel
						control={
							<Switch
								size="small"
								checked={showSubstitutions}
								onChange={(e) => setShowSubstitutions(e.target.checked)}
							/>
						}
						label={t('eventsModal.showSubstitutions', { defaultValue: 'Zobrazit střídání' })}
						labelPlacement="start"
						sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '13px', color: '#787878', fontWeight: 600 } }}
					/>
				</Box>
				<div className="msm-timeline" style={{ '--msm-home': homeColor, '--msm-away': awayColor }}>
					{/* Filtruje se až tady, ať indexy sedí na goalRunningScores (klíčované původním pořadím). */}
					{eventsData.map((ev, idx) => (
						(showSubstitutions || ev.type !== 'subst') ? renderTimelineRow(ev, idx) : null
					))}
				</div>
			</>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			transitionDuration={{ enter: 420, exit: 220 }}
			TransitionProps={{ onExited: resetState }}
			PaperProps={{ className: `msm-paper ${open ? 'is-open' : 'is-closing'}` }}
			sx={{
				'& .MuiBackdrop-root': {
					backgroundColor: 'rgba(0, 0, 0, 0.6)',
					backdropFilter: 'blur(3px)',
				},
			}}
		>
			<IconButton onClick={onClose} className="msm-close" aria-label="close">
				<CloseIcon />
			</IconButton>
			<DialogContent className="msm-content" style={{ '--msm-home': homeColor, '--msm-away': awayColor }}>
				{tabs.length > 1 && (
					<div
						className="msm-tabs"
						role="tablist"
						style={{ '--msm-tab-count': tabs.length, '--msm-tab-index': Math.max(tabs.indexOf(activeTab), 0) }}
					>
						<span className="msm-tab-slider" aria-hidden="true" />
						{tabs.map((tab) => (
							<button
								key={tab}
								type="button"
								role="tab"
								aria-selected={activeTab === tab}
								className={`msm-tab-pill ${activeTab === tab ? 'is-active' : ''}`}
								onClick={() => setActiveTab(tab)}
							>
								{tab === 'stats' && t('statisticsModal.tabStats', { defaultValue: 'Statistiky' })}
								{tab === 'timeline' && t('statisticsModal.tabTimeline', { defaultValue: 'Průběh' })}
								{tab === 'tips' && t('statisticsModal.tabTips', { defaultValue: 'Tipy' })}
							</button>
						))}
					</div>
				)}

				{activeTab === 'stats' && renderStatsTab()}
				{activeTab === 'timeline' && renderTimelineTab()}
				{activeTab === 'tips' && renderTipsTab()}
			</DialogContent>
		</Dialog>
	);
};

export default MatchStatisticsModal;
